/**
 * Recording note exports — PDF (LF-3712, FE-9) and Word/`.docx` (VALE-480).
 *
 * `GET /recordings/:id/pdf?scope=` and `GET /recordings/:id/docx?scope=` both
 * stream binary, so they go through the binary proxy (`/api/proxy/...`) rather
 * than `directApiClient` (which is JSON-only). The proxy forwards the NextAuth
 * session as a bearer token and streams the file back with its
 * `Content-Disposition`. We fetch it as a blob so we can (a) trigger a normal
 * browser download and (b) surface the `503 PDF_SERVICE_UNAVAILABLE` case as a
 * retryable toast instead of dumping the error response into a new tab (which is
 * what `window.open` would do).
 *
 * One transport for both formats on purpose: the filename fallback, the
 * `Content-Disposition` parse and the 503 handling are the same job, and a
 * second copy would drift the first time either is touched.
 */

'use client';

/** PDF export scopes (FE→BE contract). */
export type RecordingPdfScope =
  | 'summary'
  | 'summary+transcript'
  | 'summary+attachments';

/**
 * DOCX export scopes (FE→BE contract).
 *
 * **No `summary+attachments`** — the attachments scope works by merging the
 * original PDFs into the output, and there is no equivalent for a `.docx`. This
 * is a deliberate subset of {@link RecordingPdfScope}, not an oversight.
 */
export type RecordingDocxScope = 'summary' | 'summary+transcript';

/** The two download formats, and the extension each lands on disk with. */
export type RecordingExportFormat = 'pdf' | 'docx';

const FORMAT_ACCEPT: Record<RecordingExportFormat, string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
};

/**
 * Thrown when the document renderer is down (`503 PDF_SERVICE_UNAVAILABLE`).
 * The action is retryable — the caller keeps the flyout open and toasts a retry
 * hint. Named for the PDF path it was introduced on; the DOCX renderer shares
 * the same service and the same 503.
 */
export class PdfServiceUnavailableError extends Error {
  constructor() {
    super('Document generation is temporarily unavailable');
    this.name = 'PdfServiceUnavailableError';
  }
}

/** Turn a recording title into a safe-ish filename stem. */
function filenameStem(title: string | undefined, recordingId: number): string {
  const slug = (title ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return slug || `recording-${recordingId}`;
}

/** Parse a `filename="..."` out of a Content-Disposition header, if present. */
function filenameFromDisposition(disposition: string | null): string | null {
  if (!disposition) return null;
  const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(
    disposition
  );
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * Fetch one export and trigger a browser download. Resolves once the download
 * has been initiated; throws {@link PdfServiceUnavailableError} on a 503 and a
 * generic `Error` on any other non-OK response.
 */
async function downloadRecordingExport(
  format: RecordingExportFormat,
  recordingId: number,
  scope: string,
  title?: string
): Promise<void> {
  const url = `/api/proxy/recordings/${recordingId}/${format}?scope=${encodeURIComponent(
    scope
  )}`;

  const response = await fetch(url, {
    headers: { Accept: FORMAT_ACCEPT[format] }
  });

  if (response.status === 503) {
    throw new PdfServiceUnavailableError();
  }
  if (!response.ok) {
    throw new Error(
      `Failed to download ${format.toUpperCase()} (${response.status})`
    );
  }

  const blob = await response.blob();
  const filename =
    filenameFromDisposition(response.headers.get('content-disposition')) ??
    `${filenameStem(title, recordingId)}-${scope.replace('+', '-')}.${format}`;

  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/** Fetch the note PDF at `scope` and trigger a browser download. */
export function downloadRecordingPdf(
  recordingId: number,
  scope: RecordingPdfScope,
  title?: string
): Promise<void> {
  return downloadRecordingExport('pdf', recordingId, scope, title);
}

/** Fetch the note `.docx` at `scope` and trigger a browser download. */
export function downloadRecordingDocx(
  recordingId: number,
  scope: RecordingDocxScope,
  title?: string
): Promise<void> {
  return downloadRecordingExport('docx', recordingId, scope, title);
}
